// src/modules/analytics/components/UsersStatsTable.tsx
import {
  Alert,
  Box,
  Chip,
  InputAdornment,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { alpha } from '@mui/material/styles';
import type { UseUsersStatsResult } from '../hooks/use-users-stats';
import type { UserStatsSortColumn } from '../types';

interface Column {
  id: UserStatsSortColumn | 'documents_processed';
  label: string;
  sortable: boolean;
  align: 'left' | 'right';
}

const COLUMNS: Column[] = [
  { id: 'user_name', label: 'Пользователь', sortable: true, align: 'left' },
  { id: 'action_count', label: 'Вызовов', sortable: true, align: 'right' },
  { id: 'tokens_in_total', label: 'Токены вход', sortable: true, align: 'right' },
  { id: 'tokens_out_total', label: 'Токены выход', sortable: true, align: 'right' },
  { id: 'tokens_total', label: 'Токены всего', sortable: true, align: 'right' },
  { id: 'response_time_avg', label: 'Avg ответ', sortable: true, align: 'right' },
  { id: 'documents_processed', label: 'Документы', sortable: false, align: 'right' },
];

interface Props {
  state: UseUsersStatsResult;
}

export function UsersStatsTable({ state }: Props) {
  const {
    items,
    count,
    loading,
    error,
    query,
    setQuery,
    sortColumn,
    sortDirection,
    toggleSort,
    page,
    limit,
    setPage,
    setLimit,
  } = state;

  const muiDirection = sortDirection === 'ASC' ? 'asc' : 'desc';

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Box
        sx={{
          p: 2,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
          Пользователи и прожиг токенов
        </Typography>
        <TextField
          size="small"
          placeholder="Имя или email"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 240 }}
        />
      </Box>
      {error && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  fontWeight: 600,
                  fontSize: '0.68rem',
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  py: 1,
                },
              }}
            >
              {COLUMNS.map((col) => (
                <TableCell key={col.id} align={col.align}>
                  {col.sortable ? (
                    <TableSortLabel
                      active={sortColumn === col.id}
                      direction={sortColumn === col.id ? muiDirection : 'desc'}
                      onClick={() => toggleSort(col.id as UserStatsSortColumn)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.id}>
                      <Skeleton height={20} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS.length}
                  align="center"
                  sx={{ py: 4, color: 'text.secondary' }}
                >
                  Нет данных за выбранный период
                </TableCell>
              </TableRow>
            ) : (
              items.map((row) => (
                <TableRow key={row.user_id} hover>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', lineHeight: 1.3 }}>
                      {row.user_name ?? row.user_email}
                    </Typography>
                    {row.user_name && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', lineHeight: 1.3 }}
                      >
                        {row.user_email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={row.action_count}
                      size="small"
                      sx={{
                        background: (t) =>
                          alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.16 : 0.1),
                        color: (t) =>
                          t.palette.mode === 'dark' ? t.palette.primary.main : '#047857',
                        fontWeight: 600,
                        height: 20,
                        fontSize: '0.7rem',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {row.tokens_in_total.toLocaleString('ru')}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {row.tokens_out_total.toLocaleString('ru')}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: '0.82rem',
                      fontWeight: 600,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {row.tokens_total.toLocaleString('ru')}
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      component="span"
                      sx={{
                        fontSize: '0.78rem',
                        color: 'text.secondary',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {row.response_time_avg}ms
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.82rem' }}>
                    {row.documents_processed.toLocaleString('ru')}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={count}
        page={page}
        rowsPerPage={limit}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => setLimit(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[10, 20, 50, 100]}
        labelRowsPerPage="Строк на странице:"
        labelDisplayedRows={({ from, to, count: total }) =>
          `${from}–${to} из ${total !== -1 ? total : `более ${to}`}`
        }
      />
    </Paper>
  );
}
